import { DecoratorType } from '@interfaces/decorator/1.0';
import { ChangeSet } from '@generated/tcea/models/change-set';

export enum ChangeSetItemType {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  PUBLISHED = 'PUBLISHED',
}

/** Form fields not covered by catalog change sets; read-only in DRAFT/SCHEDULED. */
export const UNCOVERED_CHANGE_SET_FIELDS = ['externalId', 'alternateId', 'displayType'] as const;

export const NUTRITION_UNCOVERED_FORM_KEYS = ['nutritionTexts'] as const;

export interface ChangeSetItem {
  id: string;
  name: string;
  type: ChangeSetItemType;
  status?: string;
  dateTime?: string;
  scheduledTimestamp?: string;
}

export enum TargetEntityType {
  composite = 'COMPOSITE',
  productGroup = 'PRODUCT_GROUP',
  product = 'PRODUCT',
  modifierGroup = 'MODIFIER_GROUP',
  modifier = 'MODIFIER',
  instruction = 'INSTRUCTION',
  decorator = 'DECORATOR',
  tag = 'TAG',
  upsell = 'UPSELL',
}

export enum CatalogLookupType {
  composite = 'COMPOSITE',
  productGroup = 'PRODUCT_GROUP',
  product = 'PRODUCT',
  modifierGroup = 'MODIFIER_GROUP',
  modifier = 'MODIFIER',
  instruction = 'INSTRUCTION',
}

export enum TargetEntityOperation {
  upsert = 'UPSERT',
  delete = 'DELETE',
}

export enum LevelOfContainment {
  contains = 'CONTAINS',
  freeFrom = 'FREE_FROM',
  mayContain = 'MAY_CONTAIN',
  mayContainTracesOf = 'MAY_CONTAIN_TRACES_OF',
}

export interface AllergyTag {
  tagId: string;
  levelOfContainment: LevelOfContainment;
}

export interface PatchCatalogChangeUpsertTags {
  marketingTagIds?: string[];
  ueTaxCategoryIds?: string[];
  dietaryTagIds?: string[];
  allergyTagIds?: AllergyTag[];
}

export interface PatchCatalogChangeUpsertAlcohol {
  enabled: boolean;
  volume?: number;
}

export interface PatchCatalogChangeUpsertSodium {
  value: number;
  unit: string;
}

export interface PatchCatalogChangeUpsertCalories {
  calories?: string;
  caloricCountInput?: CaloricCountInput;
}

export interface PatchCatalogChangeUpsertNutritionInfo {
  [key: string]: any;
}

export interface CaloricCountInput {
  caloricUnit: string;
  lowerRange: number;
  higherRange?: number;
  servings?: number;
}

export interface PatchCatalogChangeUpsertUpsell {
  description: string;
  catalogEntryIds: string[]; // associated catalog entries
}

export interface ModifierGroupModification {
  modifierId: string;
  instructionId: string;
}

export interface ModifierQuantity {
  modifierId: string;
  minimumQuantity?: number;
  maximumQuantity?: number;
}

export interface PatchCatalogChangeUpsertModifierGroup {
  modifierIds: string[];
  minimumQuantity: number;
  maximumQuantity: number;
  defaultModifications?: ModifierGroupModification[];
  modifierQuantities?: ModifierQuantity[];
  hiddenModifiers?: string[];
  sku?: string;
}

export interface ProductGroupContextData {
  productGroupId: string;
  minimumRequired: number;
  maximumAllowed: number;
  defaultProductIds: string[];
}

export interface PatchCatalogChangeUpsertComposite {
  compositeType: string;
  productGroupContextData: ProductGroupContextData[];
  productGroupIds: string[];
}

export interface PatchCatalogChangeUpsertProductGroup {
  productIds: string[];
  minimumQuantity: number;
  maximumQuantity: number;
  defaultProductIds?: string[];
  isHeroProductGroup?: boolean;
}

export interface ModifierGroupContextData {
  modifierGroupId: string;
  minimumQuantity: number;
  maximumQuantity: number;
  allowedModifications: ContextModifierGroupModification[];
  defaultModifications: ContextModifierGroupModification[];
  modifierQuantities?: ModifierQuantity[];
  hiddenModifiers?: string[];
}

export interface ContextModifierGroupModification {
  modifierId: string;
  instructionIds: string[];
}

export interface PatchCatalogChangeUpsertProduct {
  modifierGroupIds: string[];
  modifierGroupContextData?: ModifierGroupContextData[];
}

export interface PatchCatalogChangeUpsertBase {
  name?: string;
  title?: string;
  description?: string;
  displayType?: string;
  externalId?: string;
  alternateId?: string;
  images?: { [key: string]: any };
}

export interface PatchCatalogChangeUpsertDecorator {
  entityId?: string;
  field: string;
  key: string;
  value?: string;
  multimedia?: boolean;
  multimediaUploadUrl?: string;
  type: DecoratorType;
  extension?: string;
}

export interface PatchCatalogChangeUpsert {
  base?: PatchCatalogChangeUpsertBase;
  decorators?: PatchCatalogChangeUpsertDecorator[];
  tag?: PatchCatalogChangeUpsertTags;
  alcohol?: PatchCatalogChangeUpsertAlcohol;
  sodium?: PatchCatalogChangeUpsertSodium;
  calories?: PatchCatalogChangeUpsertCalories;
  nutritionInfo?: PatchCatalogChangeUpsertNutritionInfo;
  upsell?: PatchCatalogChangeUpsertUpsell;
  composite?: PatchCatalogChangeUpsertComposite;
  modifierGroup?: PatchCatalogChangeUpsertModifierGroup;
  productGroup?: PatchCatalogChangeUpsertProductGroup;
  product?: PatchCatalogChangeUpsertProduct;
}

export interface PatchCatalogChangeDeleteDecorator {
  entityId: string;
  field: string;
  key: string;
  value?: string;
  multimedia?: boolean;
  multimediaUploadUrl?: string;
  type: DecoratorType;
}

export type BaseDeleteKeys =
  | 'title'
  | 'description'
  | 'name'
  | 'tag'
  | 'alcohol'
  | 'sodium'
  | 'calories'
  | 'nutritionInfo'
  | 'images'
  | 'upsell';

export type FullEntityKeys = keyof Pick<
  PatchCatalogChangeUpsert,
  'modifierGroup' | 'productGroup' | 'composite' | 'product'
>;

export interface PatchCatalogChangeDelete {
  base?: BaseDeleteKeys[];
  decorators?: PatchCatalogChangeDeleteDecorator[];
  fullEntity?: FullEntityKeys[];
}

export interface PatchCatalogChangesInput {
  catalogType: CatalogLookupType;
  upsert?: PatchCatalogChangeUpsert;
  delete?: PatchCatalogChangeDelete;
}

export interface CatalogChangeForItem {
  id: string;
  type: CatalogLookupType;
  targetEntityOperation?: TargetEntityOperation;
  targetEntityType?: TargetEntityType;
  targetField?: string;
  targetValue?:
    | string
    | PatchCatalogChangeUpsertTags
    | PatchCatalogChangeUpsertAlcohol
    | PatchCatalogChangeUpsertSodium
    | PatchCatalogChangeUpsertCalories
    | PatchCatalogChangeUpsertNutritionInfo
    | PatchCatalogChangeUpsertUpsell
    | PatchCatalogChangeUpsertModifierGroup
    | PatchCatalogChangeUpsertProductGroup
    | PatchCatalogChangeUpsertComposite
    | PatchCatalogChangeUpsertProduct;
  targetKey?: string;
}

export interface FormattedChangesForItem {
  name?: string;
  title?: string;
  description?: string;
  decorators?: any[];
  tags?: PatchCatalogChangeUpsertTags;
  alcohol?: PatchCatalogChangeUpsertAlcohol;
  images?: { [key: string]: any };
  sodium?: PatchCatalogChangeUpsertSodium;
  calories?: PatchCatalogChangeUpsertCalories;
  nutritionInfo?: PatchCatalogChangeUpsertNutritionInfo;
  upsell?: PatchCatalogChangeUpsertUpsell;
  product?: PatchCatalogChangeUpsertProduct;
  composite?: PatchCatalogChangeUpsertComposite;
  productGroup?: PatchCatalogChangeUpsertProductGroup;
  modifierGroup?: PatchCatalogChangeUpsertModifierGroup;
}

export interface FormChangeForVersion
  extends Partial<PatchCatalogChangeUpsertProduct>,
    Partial<PatchCatalogChangeUpsertComposite>,
    Partial<PatchCatalogChangeUpsertProductGroup>,
    Partial<PatchCatalogChangeUpsertModifierGroup> {
  name?: string;
  title?: string;
  description?: string;
  decorators?: any[];
  allergyTagIds?: AllergyTag[];
  dietaryTagIds?: any[];
  marketingTagIds?: any[];
  ueTaxCategoryIds?: any[];
  selectedTagIds?: any[];
  images?: { [key: string]: any };
  alcohol?: PatchCatalogChangeUpsertAlcohol;
  sodium?: PatchCatalogChangeUpsertSodium;
  calories?: string;
  caloricCountInput?: CaloricCountInput;
  nutritionInfo?: PatchCatalogChangeUpsertNutritionInfo;
  upsellData?: PatchCatalogChangeUpsertUpsell;
  modifiers?: {
    defaultModifications?: ModifierGroupModification[];
    modifierQuantities?: ModifierQuantity[];
    hiddenModifiers?: string[];
  };
}

export interface PatchCatalogChangesResponse {
  decorators: PatchCatalogChangeUpsertDecorator[];
}

export interface ReleaseChangeSetResponse {
  successResult?: {
    changeSet?: ChangeSet;
    catalogChanges?: CatalogChangeForItem[];
  };
}

export interface CatalogChangesResponse {
  _embedded: {
    catalogChanges: CatalogChangeForItem[];
  };
}

export type ItemChanges = { [key: string]: FormattedChangesForItem };

export interface ChangeSetsRequest {
  name: string;
  type: ChangeSetItemType;
  scheduledTimestamp?: string;
}

export interface ChangeSetsResponse {
  _embedded: {
    catalogChanges: ChangeSetItem[];
  };
}

export interface ChangesListResponse {
  [key: string]: CatalogChangeForItem[];
}
